import { handleRequest } from "../../axios";
import { axiosInstance } from "./JsdhNotifikaceService";
import {
    ADMIN_IOT_OBJEKTY_URL,
    ADMIN_IOT_OBJEKT_BY_ID_URL,
    ADMIN_IOT_OBJEKT_SIRENA_URL,
    ADMIN_IOT_OBJEKT_PRIJEMCI_URL,
    ADMIN_IOT_OBJEKT_PRIJEMCE_BY_ID_URL,
    ADMIN_IOT_OBJEKT_MUTE_URL,
    ADMIN_IOT_SENZOR_BY_ID_URL,
    ADMIN_IOT_SENZOR_MUTE_URL,
    ADMIN_IOT_SENZOR_MUTE_BATCH_URL,
    ADMIN_IOT_SENZOR_HISTORIE_URL,
    ADMIN_IOT_SENZOR_TEPLOTA_URL,
    ADMIN_IOT_SENZOR_COMMAND_URL,
    ADMIN_IOT_CAPABILITIES_URL,
    ADMIN_IOT_DASHBOARD_URL,
    ADMIN_IOT_SENZOR_TYPY_URL,
    ADMIN_IOT_SENZOR_TYP_BY_ID_URL,
    ADMIN_IOT_SENZOR_TYPY_REFRESH_URL,
    ADMIN_IOT_MQTT_CREDENTIALS_URL,
    ADMIN_IOT_MQTT_CREDENTIALS_PASSWORD_URL,
    ADMIN_IOT_MQTT_CREDENTIALS_REGENERATE_URL,
    ADMIN_IOT_OBJEKT_ZIGBEE_CONFIG_URL,
    ADMIN_IOT_OBJEKT_BOOTSTRAP_CONFIG_URL,
    ADMIN_IOT_SETTINGS_PRIJEMCI_URL,
    ADMIN_IOT_SETTINGS_PRIJEMCE_BY_ID_URL,
    ADMIN_IOT_DETEKCE_STAV_URL,
    ADMIN_IOT_DETEKCE_VYPNOUT_URL,
    ADMIN_IOT_DETEKCE_ZAPNOUT_URL,
} from "../url";
import type {
    IotObjekt,
    IotObjektDetail,
    CreateIotObjektRequest,
    UpdateIotObjektRequest,
    IotSenzor,
    UpdateIotSenzorRequest,
    IotMuteRequest,
    IotMuteExtendedRequest,
    IotMuteBatchRequest,
    IotMuteResponse,
    IotSirenaCommand,
    IotSenzorUdalost,
    IotPrijemce,
    JednotkaPrijemce,
    CreateIotPrijemceRequest,
    IotDashboardResponse,
    IotTeplotaResponse,
    IotSenzorTypKonfig,
    CreateIotSenzorTypRequest,
    UpdateIotSenzorTypRequest,
    MqttCredentialsResponse,
    SenzorCapabilities,
    FeatureCommandRequest,
    FeatureCommandResponse,
    IotDetekceStavResponse,
    DisableDetekceRequest,
} from "../generated";

// =====================
// OBJEKTY
// =====================

const getAllObjekty = () => {
    return handleRequest<IotObjekt[]>(() =>
        axiosInstance.get(ADMIN_IOT_OBJEKTY_URL)
    );
};

const getObjektById = (id: number) => {
    return handleRequest<IotObjektDetail>(() =>
        axiosInstance.get(ADMIN_IOT_OBJEKT_BY_ID_URL(id))
    );
};

const createObjekt = (data: CreateIotObjektRequest) => {
    return handleRequest<IotObjekt>(() =>
        axiosInstance.post(ADMIN_IOT_OBJEKTY_URL, data)
    );
};

const updateObjekt = (id: number, data: UpdateIotObjektRequest) => {
    return handleRequest<IotObjekt>(() =>
        axiosInstance.put(ADMIN_IOT_OBJEKT_BY_ID_URL(id), data)
    );
};

const deleteObjekt = (id: number) => {
    return handleRequest<void>(() =>
        axiosInstance.delete(ADMIN_IOT_OBJEKT_BY_ID_URL(id))
    );
};

// =====================
// SENZORY
// =====================

const getSenzorById = (id: number) => {
    return handleRequest<IotSenzor>(() =>
        axiosInstance.get(ADMIN_IOT_SENZOR_BY_ID_URL(id))
    );
};

const updateSenzor = (id: number, data: UpdateIotSenzorRequest) => {
    return handleRequest<IotSenzor>(() =>
        axiosInstance.put(ADMIN_IOT_SENZOR_BY_ID_URL(id), data)
    );
};

const muteSenzor = (id: number, data: IotMuteRequest | IotMuteExtendedRequest) => {
    return handleRequest<IotSenzor>(() =>
        axiosInstance.post(ADMIN_IOT_SENZOR_MUTE_URL(id), data)
    );
};

const unmuteSenzor = (id: number) => {
    return handleRequest<IotSenzor>(() =>
        axiosInstance.delete(ADMIN_IOT_SENZOR_MUTE_URL(id))
    );
};

// =====================
// HROMADNE MUTE
// =====================

const muteBatchSenzory = (data: IotMuteBatchRequest) => {
    return handleRequest<IotMuteResponse>(() =>
        axiosInstance.post(ADMIN_IOT_SENZOR_MUTE_BATCH_URL, data)
    );
};

const muteObjekt = (objektId: number, data: IotMuteExtendedRequest) => {
    return handleRequest<IotMuteResponse>(() =>
        axiosInstance.post(ADMIN_IOT_OBJEKT_MUTE_URL(objektId), data)
    );
};

const unmuteObjekt = (objektId: number) => {
    return handleRequest<IotMuteResponse>(() =>
        axiosInstance.delete(ADMIN_IOT_OBJEKT_MUTE_URL(objektId))
    );
};

const getSenzorHistorie = (id: number, limit?: number) => {
    const params = limit ? { limit } : {};
    return handleRequest<IotSenzorUdalost[]>(() =>
        axiosInstance.get(ADMIN_IOT_SENZOR_HISTORIE_URL(id), { params })
    );
};

const getSenzorTeplota = (id: number, limit?: number) => {
    const params = limit ? { limit } : {};
    return handleRequest<IotTeplotaResponse>(() =>
        axiosInstance.get(ADMIN_IOT_SENZOR_TEPLOTA_URL(id), { params })
    );
};

// =====================
// SIRENA
// =====================

const startSirena = (objektId: number, senzorId: number) => {
    const data: IotSirenaCommand = { action: 'START' };
    return handleRequest<void>(() =>
        axiosInstance.post(ADMIN_IOT_OBJEKT_SIRENA_URL(objektId, senzorId), data)
    );
};

const stopSirena = (objektId: number, senzorId: number) => {
    const data: IotSirenaCommand = { action: 'STOP' };
    return handleRequest<void>(() =>
        axiosInstance.delete(ADMIN_IOT_OBJEKT_SIRENA_URL(objektId, senzorId))
    );
};

// =====================
// PRIJEMCI
// =====================

const getObjektPrijemci = (objektId: number) => {
    return handleRequest<IotPrijemce[]>(() =>
        axiosInstance.get(ADMIN_IOT_OBJEKT_PRIJEMCI_URL(objektId))
    );
};

const addObjektPrijemce = (objektId: number, data: CreateIotPrijemceRequest) => {
    return handleRequest<IotPrijemce>(() =>
        axiosInstance.post(ADMIN_IOT_OBJEKT_PRIJEMCI_URL(objektId), data)
    );
};

const removeObjektPrijemce = (objektId: number, prijemceId: number) => {
    return handleRequest<void>(() =>
        axiosInstance.delete(ADMIN_IOT_OBJEKT_PRIJEMCE_BY_ID_URL(objektId, prijemceId))
    );
};

// =====================
// GLOBALNI PRIJEMCI JEDNOTKY
// =====================

const getGlobalniPrijemci = () => {
    return handleRequest<JednotkaPrijemce[]>(() =>
        axiosInstance.get(ADMIN_IOT_SETTINGS_PRIJEMCI_URL)
    );
};

const addGlobalniPrijemce = (data: CreateIotPrijemceRequest) => {
    return handleRequest<JednotkaPrijemce>(() =>
        axiosInstance.post(ADMIN_IOT_SETTINGS_PRIJEMCI_URL, data)
    );
};

const removeGlobalniPrijemce = (prijemceId: number) => {
    return handleRequest<void>(() =>
        axiosInstance.delete(ADMIN_IOT_SETTINGS_PRIJEMCE_BY_ID_URL(prijemceId))
    );
};

// =====================
// DASHBOARD
// =====================

const getDashboard = () => {
    return handleRequest<IotDashboardResponse>(() =>
        axiosInstance.get(ADMIN_IOT_DASHBOARD_URL)
    );
};

// =====================
// KONFIGURACE TYPU SENZORU
// =====================

const getAllSenzorTypy = () => {
    return handleRequest<IotSenzorTypKonfig[]>(() =>
        axiosInstance.get(ADMIN_IOT_SENZOR_TYPY_URL)
    );
};

const getSenzorTypById = (id: number) => {
    return handleRequest<IotSenzorTypKonfig>(() =>
        axiosInstance.get(ADMIN_IOT_SENZOR_TYP_BY_ID_URL(id))
    );
};

const createSenzorTyp = (data: CreateIotSenzorTypRequest) => {
    return handleRequest<IotSenzorTypKonfig>(() =>
        axiosInstance.post(ADMIN_IOT_SENZOR_TYPY_URL, data)
    );
};

const updateSenzorTyp = (id: number, data: UpdateIotSenzorTypRequest) => {
    return handleRequest<IotSenzorTypKonfig>(() =>
        axiosInstance.put(ADMIN_IOT_SENZOR_TYP_BY_ID_URL(id), data)
    );
};

const deleteSenzorTyp = (id: number) => {
    return handleRequest<void>(() =>
        axiosInstance.delete(ADMIN_IOT_SENZOR_TYP_BY_ID_URL(id))
    );
};

const refreshSenzorTypyCache = () => {
    return handleRequest<void>(() =>
        axiosInstance.post(ADMIN_IOT_SENZOR_TYPY_REFRESH_URL)
    );
};

// =====================
// MQTT CREDENTIALS
// =====================

const getMqttCredentials = () => {
    return handleRequest<MqttCredentialsResponse>(() =>
        axiosInstance.get(ADMIN_IOT_MQTT_CREDENTIALS_URL)
    );
};

const getMqttPassword = () => {
    return handleRequest<MqttCredentialsResponse>(() =>
        axiosInstance.get(ADMIN_IOT_MQTT_CREDENTIALS_PASSWORD_URL)
    );
};

const regenerateMqttPassword = () => {
    return handleRequest<MqttCredentialsResponse>(() =>
        axiosInstance.post(ADMIN_IOT_MQTT_CREDENTIALS_REGENERATE_URL)
    );
};

const downloadZigbeeConfig = (objektId: number) => {
    return handleRequest<string>(() =>
        axiosInstance.get(ADMIN_IOT_OBJEKT_ZIGBEE_CONFIG_URL(objektId), {
            responseType: 'text',
        })
    );
};

const downloadBootstrapConfig = (objektId: number) => {
    return handleRequest<string>(() =>
        axiosInstance.get(ADMIN_IOT_OBJEKT_BOOTSTRAP_CONFIG_URL(objektId), {
            responseType: 'text',
        })
    );
};

// =====================
// CAPABILITIES
// =====================

const getObjektCapabilities = (objektId: number) => {
    return handleRequest<SenzorCapabilities[]>(() =>
        axiosInstance.get(ADMIN_IOT_CAPABILITIES_URL(objektId))
    );
};

const sendFeatureCommand = (senzorId: number, data: FeatureCommandRequest) => {
    return handleRequest<FeatureCommandResponse>(() =>
        axiosInstance.post(ADMIN_IOT_SENZOR_COMMAND_URL(senzorId), data)
    );
};

// =====================
// DETEKCE - globalni vypnuti
// =====================

const getIotDetekceStav = () => {
    return handleRequest<IotDetekceStavResponse>(() =>
        axiosInstance.get(ADMIN_IOT_DETEKCE_STAV_URL)
    );
};

const vypnoutIotDetekci = (data: DisableDetekceRequest) => {
    return handleRequest<IotDetekceStavResponse>(() =>
        axiosInstance.post(ADMIN_IOT_DETEKCE_VYPNOUT_URL, data)
    );
};

const zapnoutIotDetekci = () => {
    return handleRequest<IotDetekceStavResponse>(() =>
        axiosInstance.post(ADMIN_IOT_DETEKCE_ZAPNOUT_URL)
    );
};

// =====================
// EXPORT
// =====================

const IotService = {
    // Objekty
    getAllObjekty,
    getObjektById,
    createObjekt,
    updateObjekt,
    deleteObjekt,
    // Senzory
    getSenzorById,
    updateSenzor,
    muteSenzor,
    unmuteSenzor,
    getSenzorHistorie,
    getSenzorTeplota,
    // Hromadne mute
    muteBatchSenzory,
    muteObjekt,
    unmuteObjekt,
    // Sirena
    startSirena,
    stopSirena,
    // Prijemci objektu
    getObjektPrijemci,
    addObjektPrijemce,
    removeObjektPrijemce,
    // Globalni prijemci jednotky
    getGlobalniPrijemci,
    addGlobalniPrijemce,
    removeGlobalniPrijemce,
    // Dashboard
    getDashboard,
    // Konfigurace typu senzoru
    getAllSenzorTypy,
    getSenzorTypById,
    createSenzorTyp,
    updateSenzorTyp,
    deleteSenzorTyp,
    refreshSenzorTypyCache,
    // MQTT Credentials
    getMqttCredentials,
    getMqttPassword,
    regenerateMqttPassword,
    downloadZigbeeConfig,
    downloadBootstrapConfig,
    // Capabilities
    getObjektCapabilities,
    sendFeatureCommand,
    // Detekce - globalni vypnuti
    getIotDetekceStav,
    vypnoutIotDetekci,
    zapnoutIotDetekci,
};

export default IotService;
